import { __rest, __assign } from 'tslib';
import { jsxs, jsx, Fragment } from '@emotion/react/jsx-runtime';
import { useRef, useLayoutEffect, useEffect } from 'react';
import { useTheme } from '../hooks/useTheme.js';
import { renderProp } from '../utils/renderProp.js';
import { Box } from '../Box/index.js';

var ScrollShadow = function (props) { return (jsx(Box, __assign({}, props, { sx: {
        bottom: 0,
        left: 0,
        pointerEvents: "none",
        position: "absolute",
        right: 0,
        top: 0,
        transition: "all .2s ease-out",
    } }))); };
var getHeightDiff = function (innerRef, scrollRef) {
    var _a, _b;
    return (((_a = innerRef.current) === null || _a === void 0 ? void 0 : _a.offsetHeight) || 0) -
        (((_b = scrollRef.current) === null || _b === void 0 ? void 0 : _b.offsetHeight) || 0);
};
var getWidthDiff = function (innerRef, scrollRef) {
    var _a, _b;
    return ((((_a = innerRef.current) === null || _a === void 0 ? void 0 : _a.offsetWidth) || 0) - (((_b = scrollRef.current) === null || _b === void 0 ? void 0 : _b.offsetWidth) || 0));
};
var useSetShadow = function (_a) {
    var isScrolling = _a.isScrolling, outerRef = _a.outerRef, innerRef = _a.innerRef, scrollRef = _a.scrollRef, isVertical = _a.isVertical;
    return function () {
        // turn on/off scroll shadows as appropriate
        if (!isScrolling.current) {
            var diff_1 = isVertical
                ? getHeightDiff(innerRef, scrollRef)
                : getWidthDiff(innerRef, scrollRef);
            window.requestAnimationFrame(function () {
                var outer = outerRef.current;
                var scroll = scrollRef.current;
                if (outer && scroll) {
                    if (isVertical) {
                        outer.setAttribute("data-scroll-up", "".concat(scroll.scrollTop > 0));
                        outer.setAttribute("data-scroll-down", "".concat(scroll.scrollTop < diff_1));
                    }
                    else {
                        outer.setAttribute("data-scroll-left", "".concat(scroll.scrollLeft > 0));
                        outer.setAttribute("data-scroll-right", "".concat(scroll.scrollLeft < diff_1));
                    }
                }
                isScrolling.current = false;
            });
            isScrolling.current = true;
        }
    };
};
var hideScrollbarSx = {
    // https://stackoverflow.com/a/49278385/1315176
    /* Firefox */
    scrollbarWidth: "none",
    scrollbarHeight: "none",
    /* Internet Explorer 10+ */
    msOverflowStyle: "none",
    /* WebKit */
    "&::-webkit-scrollbar": {
        width: 0,
        height: 0,
    },
};
var Scrollable = function (_a) {
    var children = _a.children, _b = _a.hideScrollbar, hideScrollbar = _b === void 0 ? true : _b, _c = _a.direction, direction = _c === void 0 ? "vertical" : _c, props = __rest(_a, ["children", "hideScrollbar", "direction"]);
    var handleStyleProps = useTheme().handleStyleProps;
    var innerRef = useRef(null);
    var outerRef = useRef(null);
    var scrollRef = useRef(null);
    var isScrolling = useRef(false);
    var isVertical = direction === "vertical";
    var setShadows = useSetShadow({
        isScrolling: isScrolling,
        outerRef: outerRef,
        innerRef: innerRef,
        scrollRef: scrollRef,
        isVertical: isVertical,
    });
    useLayoutEffect(function () {
        // turn on the bottom scroll prompt to start if appropriate
        if (hideScrollbar)
            setShadows();
    }, []);
    useEffect(function () {
        // listen for scroll event and update scroll prompts
        if (hideScrollbar) {
            var scrollBox_1 = scrollRef.current;
            scrollBox_1 === null || scrollBox_1 === void 0 ? void 0 : scrollBox_1.addEventListener("scroll", setShadows);
            return function () { return scrollBox_1 === null || scrollBox_1 === void 0 ? void 0 : scrollBox_1.removeEventListener("scroll", setShadows); };
        }
    }, []);
    var _d = handleStyleProps(props, {
        themeKey: "scrollable",
        baseStyle: {
            overflow: "hidden",
            position: "relative",
            boxSizing: "border-box",
            height: "100%",
            width: "100%",
        },
    }), css = _d.css, forwardOuterProps = __rest(_d, ["css"]);
    var outerCss = __assign(__assign({}, css), { "--shadow-top-color": css.backgroundColor, "--shadow-bottom-color": css.backgroundColor });
    var scrollSx = __assign({ height: "100%", width: "100%", overflow: "auto" }, (hideScrollbar && hideScrollbarSx));
    var innerScrollSx = __assign({}, (!isVertical && { width: "fit-content" }));
    var context = { scrollRef: scrollRef, innerRef: innerRef };
    return (jsxs("div", __assign({ "data-outer": true, css: outerCss }, forwardOuterProps, { ref: outerRef }, { children: [jsx(Box, __assign({ "data-scroll": true, sx: scrollSx, ref: scrollRef }, { children: jsx(Box, __assign({ "data-inner-scroll": true, sx: innerScrollSx, ref: innerRef }, { children: renderProp({
                        prop: children,
                        context: context,
                    }) })) })), hideScrollbar && (jsxs(Fragment, { children: [jsx(ScrollShadow, { "data-scroll-shadow-top": true }), jsx(ScrollShadow, { "data-scroll-shadow-bottom": true })] }))] })));
};

export { Scrollable };
//# sourceMappingURL=Scrollable.js.map
